import React, { useContext, useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import {
  createEntity,
  createEntityModel,
  createMapData,
  deleteEntityModel,
  getCustomPinIcons,
  getEntityModels,
  getMaps,
  updateEntityModel,
} from '../services/api';
import GlobalContext, {
  useOrganizationChange,
} from '../contexts/GlobalContext';

const FIELD_TYPES = [
  { value: 'string', label: 'String' },
  { value: 'integer', label: 'Integer' },
  { value: 'float', label: 'Float' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'text', label: 'Text' },
];

const slugify = label =>
  (label || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const emptyField = (order = 0) => ({
  key: '',
  label: '',
  field_type: 'string',
  enumText: '',
  required: false,
  visible_on_map: false,
  order,
  keyTouched: false,
});

const EntityModelManager = () => {
  const { selectedOrganization } = useContext(GlobalContext);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assignableAsStar, setAssignableAsStar] = useState(false);
  const [fields, setFields] = useState([emptyField(0)]);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [entityDialogOpen, setEntityDialogOpen] = useState(false);
  const [maps, setMaps] = useState([]);
  const [entityName, setEntityName] = useState('');
  const [selectedModelId, setSelectedModelId] = useState('');
  const [selectedMapId, setSelectedMapId] = useState('');
  const [entityValues, setEntityValues] = useState({});
  const [icons, setIcons] = useState([]);
  const [pinIconId, setPinIconId] = useState(null);

  const orgId =
    selectedOrganization?.id ??
    (typeof selectedOrganization === 'number' ? selectedOrganization : null);

  const canEdit = Boolean(orgId);

  const loadModels = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getEntityModels();
      const list = response?.results || response || [];
      setModels(
        list.filter(model => Number(model.organization) === Number(orgId))
      );
    } catch (err) {
      console.error('Error loading entity models:', err);
      setError('Failed to load entity models.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orgId) {
      loadModels();
    }
  }, [orgId]);

  useOrganizationChange(() => {
    loadModels();
  }, []);

  const selectedModel = models.find(m => m.id === Number(selectedModelId));

  const openCreateEntity = (model = null) => {
    setSelectedModelId(model?.id || '');
    setEntityName('');
    setSelectedMapId('');
    setEntityValues({});
    setEntityDialogOpen(true);
    getMaps()
      .then(response => {
        const list = response?.results || response || [];
        setMaps(list.filter(map => Number(map.organization) === Number(orgId)));
      })
      .catch(err => console.error('Error loading maps:', err));
  };

  const handleCreateEntity = async () => {
    setError(null);
    if (!selectedModel) {
      setError('Choose an entity model.');
      return;
    }
    try {
      if (selectedMapId) {
        if (!selectedModel.pin_icon?.id) {
          setError(
            'This entity model has no icon. Link an icon before placing it on a map.'
          );
          return;
        }
        const map = maps.find(m => m.id === Number(selectedMapId));
        const lon = map?.center_longitude ?? 0;
        const lat = map?.center_latitude ?? 0;
        await createMapData({
          map: Number(selectedMapId),
          name: entityName.trim() || selectedModel.name,
          description: '',
          shape_type: 'point',
          custom_pin_icon_id: selectedModel.pin_icon.id,
          geometry_data: { coordinates: [lon, lat] },
          style_data: { scale: 0.1, rotation: 0 },
          entity: {
            entity_model_id: selectedModel.id,
            values: entityValues,
          },
        });
      } else {
        await createEntity({
          entity_model_id: selectedModel.id,
          values: entityValues,
        });
      }
      setEntityDialogOpen(false);
    } catch (err) {
      const data = err.response?.data;
      const message =
        data?.error ||
        data?.values ||
        data?.detail ||
        (typeof data === 'object' ? JSON.stringify(data) : null) ||
        'Failed to create entity.';
      setError(typeof message === 'string' ? message : JSON.stringify(message));
    }
  };

  const loadIcons = async (keepIconId = null) => {
    try {
      const response = await getCustomPinIcons();
      const list = response?.results || response || [];
      setIcons(
        list.filter(icon => {
          if (Number(icon.organization) !== Number(orgId)) return false;
          if (!icon.entity_model) return true;
          return Boolean(keepIconId) && icon.id === keepIconId;
        })
      );
    } catch (err) {
      console.error('Error loading pin icons:', err);
    }
  };

  const openCreate = () => {
    setError(null);
    setEditingModel(null);
    setName('');
    setDescription('');
    setAssignableAsStar(false);
    setFields([emptyField(0)]);
    setPinIconId(null);
    setDialogOpen(true);
    loadIcons();
  };

  const openEdit = model => {
    setError(null);
    setEditingModel(model);
    setName(model.name);
    setDescription(model.description || '');
    setAssignableAsStar(Boolean(model.assignable_as_star));
    setFields(
      (model.fields || []).length
        ? model.fields.map((field, index) => ({
            ...field,
            enumText: (field.enum || []).join('\n'),
            keyTouched: true,
            order: field.order ?? index,
          }))
        : [emptyField(0)]
    );
    setPinIconId(model.pin_icon?.id || null);
    setDialogOpen(true);
    loadIcons(model.pin_icon?.id);
  };

  const handleFieldChange = (index, patch) => {
    setFields(prev =>
      prev.map((field, i) => {
        if (i !== index) return field;
        const next = { ...field, ...patch };
        if ('label' in patch && !field.keyTouched) {
          next.key = slugify(patch.label);
        }
        if ('key' in patch) {
          next.keyTouched = true;
        }
        return next;
      })
    );
  };

  const addField = () => {
    setFields(prev => [...prev, emptyField(prev.length)]);
  };

  const removeField = index => {
    setFields(prev =>
      prev
        .filter((_, i) => i !== index)
        .map((field, i) => ({ ...field, order: i }))
    );
  };

  const moveField = (index, direction) => {
    setFields(prev => {
      const next = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((field, i) => ({ ...field, order: i }));
    });
  };

  const handleSave = async () => {
    setError(null);
    const payload = {
      organization: orgId,
      name,
      description,
      assignable_as_star: assignableAsStar,
      fields: fields
        .filter(field => field.label && field.key)
        .map((field, index) => ({
          key: field.key,
          label: field.label,
          field_type: field.field_type,
          enum:
            field.field_type === 'string'
              ? (field.enumText || '')
                  .split('\n')
                  .map(option => option.trim())
                  .filter(Boolean)
              : [],
          required: Boolean(field.required),
          visible_on_map: Boolean(field.visible_on_map),
          order: index,
        })),
      pin_icon_id: pinIconId,
    };

    try {
      if (editingModel) {
        await updateEntityModel(editingModel.id, payload);
      } else {
        await createEntityModel(payload);
      }
      setDialogOpen(false);
      await loadModels();
    } catch (err) {
      const data = err.response?.data;
      const message =
        data?.error ||
        data?.detail ||
        (typeof data === 'object' ? JSON.stringify(data) : null) ||
        'Failed to save entity model.';
      setError(message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteEntityModel(deleteTarget.id);
      setDeleteTarget(null);
      await loadModels();
    } catch (err) {
      const message =
        err.response?.data?.error ||
        'Cannot delete this entity model. It may still have entities.';
      setError(message);
      setDeleteTarget(null);
    }
  };

  const columns = [
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 160 },
    {
      field: 'pin_icon',
      headerName: 'Icon',
      width: 160,
      valueGetter: (value, row) => row.pin_icon?.name || '',
      renderCell: params =>
        params.row.pin_icon ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: '100%',
            }}
          >
            {params.row.pin_icon.icon_image_url ? (
              <Box
                component="img"
                src={params.row.pin_icon.icon_image_url}
                alt=""
                sx={{ width: 24, height: 24, objectFit: 'contain' }}
              />
            ) : null}
            <span>{params.row.pin_icon.name}</span>
          </Box>
        ) : (
          '—'
        ),
    },
    {
      field: 'field_count',
      headerName: 'Fields',
      width: 100,
      valueGetter: (value, row) => row.field_count ?? row.fields?.length ?? 0,
    },
    {
      field: 'updated_at',
      headerName: 'Updated',
      width: 180,
      valueFormatter: value => (value ? new Date(value).toLocaleString() : ''),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 140,
      getActions: params =>
        canEdit
          ? [
              <GridActionsCellItem
                key="instance"
                icon={<AddIcon />}
                label="New entity"
                onClick={() => openCreateEntity(params.row)}
              />,
              <GridActionsCellItem
                key="edit"
                icon={<EditIcon />}
                label="Edit"
                onClick={() => openEdit(params.row)}
              />,
              <GridActionsCellItem
                key="delete"
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => setDeleteTarget(params.row)}
              />,
            ]
          : [],
    },
  ];

  return (
    <Box>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Entity Models
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Define schemas such as CenTrak Star or Virtual Wall. Each icon owns one
        model; pick an icon on the map to fill that schema.
      </Typography>
      {error && !dialogOpen && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          color="primary"
          startIcon={<AddIcon />}
          onClick={openCreate}
          variant="contained"
          disabled={!canEdit}
        >
          New Entity Model
        </Button>
        <Button
          color="secondary"
          startIcon={<AddIcon />}
          onClick={() => openCreateEntity()}
          variant="contained"
          disabled={!canEdit || models.length === 0}
        >
          New Entity
        </Button>
      </Box>
      <Box sx={{ height: 520, width: '100%' }}>
        <DataGrid
          rows={models}
          columns={columns}
          loading={loading}
          disableRowSelectionOnClick
          pageSizeOptions={[5, 10, 25]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </Box>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingModel ? 'Edit Entity Model' : 'New Entity Model'}
        </DialogTitle>
        {error && (
          <Alert
            severity="error"
            sx={{ mx: 3, mb: 2, overflowWrap: 'anywhere' }}
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        <DialogContent>
          <TextField
            margin="dense"
            label="Name"
            fullWidth
            required
            value={name}
            onChange={e => setName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            minRows={2}
            value={description}
            onChange={e => setDescription(e.target.value)}
          />
          <FormControlLabel
            label="Assignable as star"
            control={
              <Checkbox
                checked={assignableAsStar}
                onChange={e => setAssignableAsStar(e.target.checked)}
              />
            }
          />
          <Autocomplete
            sx={{ mt: 1 }}
            options={icons}
            getOptionLabel={option => option.name || ''}
            value={icons.find(icon => icon.id === pinIconId) || null}
            onChange={(event, newValue) => {
              setPinIconId(newValue?.id || null);
            }}
            noOptionsText="No unassigned icons"
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                <Avatar
                  src={option.icon_image_url || ''}
                  alt=""
                  variant="square"
                  sx={{ width: 28, height: 28, mr: 1 }}
                />
                {option.name}
              </Box>
            )}
            renderInput={params => (
              <TextField
                {...params}
                label="Pin icon"
                helperText="Optional. Only unassigned icons are listed."
              />
            )}
          />
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            Fields
          </Typography>
          {fields.map((field, index) => (
            <Box
              key={index}
              role="group"
              aria-label={`Field ${index + 1}${field.label ? `: ${field.label}` : ''}`}
              sx={{
                mb: 2,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  bgcolor: 'action.hover',
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ overflowWrap: 'anywhere' }}
                >
                  Field {index + 1}
                  {field.label ? ` · ${field.label}` : ''}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: 'minmax(0, 1fr)',
                    sm: 'minmax(0, 1fr) minmax(0, 1fr) 120px',
                  },
                  gap: 2,
                  alignItems: 'start',
                }}
              >
                <TextField
                  size="small"
                  label="Label"
                  value={field.label}
                  onChange={e =>
                    handleFieldChange(index, { label: e.target.value })
                  }
                  sx={{ minWidth: 0 }}
                />
                <TextField
                  size="small"
                  label="Key"
                  value={field.key}
                  onChange={e =>
                    handleFieldChange(index, { key: e.target.value })
                  }
                  sx={{ minWidth: 0 }}
                />
                <FormControl size="small" sx={{ minWidth: 120 }}>
                  <InputLabel>Type</InputLabel>
                  <Select
                    label="Type"
                    value={field.field_type}
                    onChange={e =>
                      handleFieldChange(index, { field_type: e.target.value })
                    }
                  >
                    {FIELD_TYPES.map(type => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {field.field_type === 'string' && (
                  <TextField
                    size="small"
                    label="Allowed values"
                    multiline
                    minRows={2}
                    value={field.enumText || ''}
                    onChange={e =>
                      handleFieldChange(index, { enumText: e.target.value })
                    }
                    helperText="One option per line. Leave empty to allow any text."
                    sx={{ minWidth: 0, gridColumn: '1 / -1' }}
                  />
                )}
              </Box>
              <Box
                sx={{
                  px: 2,
                  py: 1,
                  display: 'flex',
                  gap: 1,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  borderTop: 1,
                  borderColor: 'divider',
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(field.required)}
                      onChange={e =>
                        handleFieldChange(index, { required: e.target.checked })
                      }
                    />
                  }
                  label="Required"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={Boolean(field.visible_on_map)}
                      onChange={e =>
                        handleFieldChange(index, {
                          visible_on_map: e.target.checked,
                        })
                      }
                    />
                  }
                  label="Visible on map"
                />
                <Box sx={{ display: 'flex', gap: 0.5, ml: 'auto' }}>
                  <IconButton
                    size="small"
                    aria-label={`Move field ${index + 1} up`}
                    disabled={index === 0}
                    onClick={() => moveField(index, -1)}
                  >
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Move field ${index + 1} down`}
                    disabled={index === fields.length - 1}
                    onClick={() => moveField(index, 1)}
                  >
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label={`Delete field ${index + 1}`}
                    onClick={() => removeField(index)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          ))}
          <Button startIcon={<AddIcon />} onClick={addField}>
            Add field
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!name.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={entityDialogOpen}
        onClose={() => setEntityDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>New Entity</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="dense">
            <InputLabel>Entity model</InputLabel>
            <Select
              label="Entity model"
              value={selectedModelId}
              onChange={e => {
                setSelectedModelId(e.target.value);
                setEntityValues({});
                const next = models.find(m => m.id === Number(e.target.value));
                if (!next?.pin_icon) {
                  setSelectedMapId('');
                }
              }}
            >
              {models.map(model => (
                <MenuItem key={model.id} value={model.id}>
                  {model.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="dense"
            label="Name on map"
            fullWidth
            value={entityName}
            onChange={e => setEntityName(e.target.value)}
            helperText="Used if you place this entity on a map"
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>Map (optional)</InputLabel>
            <Select
              label="Map (optional)"
              value={selectedMapId}
              onChange={e => setSelectedMapId(e.target.value)}
              disabled={Boolean(selectedModel) && !selectedModel.pin_icon}
            >
              <MenuItem value="">None — create without a pin</MenuItem>
              {maps.map(map => (
                <MenuItem key={map.id} value={map.id}>
                  {map.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selectedModel && !selectedModel.pin_icon ? (
            <Typography variant="caption" color="text.secondary">
              Link an icon to this model before placing it on a map.
            </Typography>
          ) : null}
          {(selectedModel?.fields || []).map(field =>
            field.enum?.length ? (
              <TextField
                key={field.key}
                select
                margin="dense"
                label={field.label}
                required={field.required}
                fullWidth
                value={entityValues[field.key] ?? ''}
                onChange={e =>
                  setEntityValues({
                    ...entityValues,
                    [field.key]: e.target.value,
                  })
                }
              >
                <MenuItem value="">
                  {field.required ? 'Select a value' : 'None'}
                </MenuItem>
                {field.enum.map(option => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </TextField>
            ) : field.field_type === 'boolean' ? (
              <FormControlLabel
                key={field.key}
                sx={{ display: 'block', mt: 1 }}
                control={
                  <Checkbox
                    checked={Boolean(entityValues[field.key])}
                    onChange={e =>
                      setEntityValues({
                        ...entityValues,
                        [field.key]: e.target.checked,
                      })
                    }
                  />
                }
                label={`${field.label}${field.required ? ' *' : ''}`}
              />
            ) : (
              <TextField
                key={field.key}
                margin="dense"
                label={field.label}
                required={field.required}
                fullWidth
                type={
                  field.field_type === 'integer' || field.field_type === 'float'
                    ? 'number'
                    : 'text'
                }
                multiline={field.field_type === 'text'}
                minRows={field.field_type === 'text' ? 2 : undefined}
                value={entityValues[field.key] ?? ''}
                onChange={e =>
                  setEntityValues({
                    ...entityValues,
                    [field.key]: e.target.value,
                  })
                }
              />
            )
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEntityDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateEntity}
            disabled={!selectedModelId}
          >
            Create Entity
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
      >
        <DialogTitle>Delete entity model</DialogTitle>
        <DialogContent>
          Delete “{deleteTarget?.name}”? This fails if any map items still use
          it.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EntityModelManager;
